import { format, parseISO } from "date-fns";
import type {
  EmploymentDetails,
  PeriodStartSetting,
} from "../../../../employment/employmentApi";
import { validateDateInput } from "../../../../../common/helpers/formatDateTime";
import { periodStartBaseline } from "../../../../employment/periodStartSettings";

export type PeriodStartFormValues = {
  from: Date | null;
  periodStart: Date | null;
};
export type PeriodStartRow = {
  setting: PeriodStartSetting;
  isBaseline: boolean;
};

/** The baseline is a display fallback, never a synthetic entry sent to the backend. */
export const periodStartRows = (
  details: EmploymentDetails,
): PeriodStartRow[] => {
  const settings = details.periodStartSettings ?? [];
  const rows = settings.map((setting) => ({ setting, isBaseline: false }));
  const baseline = periodStartBaseline(details);
  if (
    baseline !== null &&
    !settings.some((setting) => setting.from === details.agreedStart)
  ) {
    rows.push({
      setting: { from: details.agreedStart, periodStart: baseline },
      isBaseline: true,
    });
  }
  return rows.sort((a, b) => a.setting.from.localeCompare(b.setting.from));
};

export const toPeriodStartForm = (
  setting?: PeriodStartSetting,
): PeriodStartFormValues => ({
  from: setting ? parseISO(setting.from) : null,
  periodStart: setting ? parseISO(setting.periodStart) : null,
});

export const periodStartValidationErrors = (
  values: PeriodStartFormValues,
  details: EmploymentDetails,
  originalFrom?: string,
): { from?: string; periodStart?: string } => {
  const errors: { from?: string; periodStart?: string } = {};
  if (!values.from || !validateDateInput(values.from))
    errors.from = "common:invalidDate";
  if (!values.periodStart || !validateDateInput(values.periodStart))
    errors.periodStart = "common:invalidDate";
  if (!errors.from) {
    const from = format(values.from!, "yyyy-MM-dd");
    if (
      from < details.agreedStart ||
      (details.agreedEnd && from > details.agreedEnd)
    ) {
      errors.from = "periodStartEffectiveDateOutsideEmployment";
    } else if (
      details.periodStartSettings?.some(
        (setting) => setting.from === from && setting.from !== originalFrom,
      )
    ) {
      errors.from = "periodStartDuplicateEffectiveDate";
    }
    if (
      !errors.periodStart &&
      format(values.periodStart!, "yyyy-MM-dd") > from
    ) {
      errors.periodStart = "periodStartAnchorAfterEffectiveDate";
    }
  }
  return errors;
};

export const toPeriodStartSetting = (
  values: PeriodStartFormValues,
): PeriodStartSetting => ({
  from: format(values.from!, "yyyy-MM-dd"),
  periodStart: format(values.periodStart!, "yyyy-MM-dd"),
});
